import { useState, useEffect } from 'react';
import { Plus, Search, Copy, Ban, Trash2, AlertTriangle, X } from 'lucide-react';
import DashboardLayout from './DashboardLayout';
import { fetchAliasList, fetchDailySendStats, updateAliasStatus, deleteAliases, createAlias } from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import { useTranslation } from 'react-i18next';
import { isAliasLimitReached } from './planPrompts';
import { createAnalyticsFlowId, getAnalyticsSessionId, trackProductEvent } from '../../services/analytics';

interface AliasItem {
  alias: string;
  enable: boolean;
  time: number;
  fwd: number;
}

interface DailyStats {
  [key: string]: number;
}

const AliasesPage = () => {
  const { t } = useTranslation();
  const [aliases, setAliases] = useState<AliasItem[]>([]);
  const [dailyStats, setDailyStats] = useState<DailyStats>({});
  const [totalAliases, setTotalAliases] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAliases, setSelectedAliases] = useState<string[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newAlias, setNewAlias] = useState('');
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isAliasLimitModalOpen, setIsAliasLimitModalOpen] = useState(false);
  const [aliasCreateFlowId, setAliasCreateFlowId] = useState<string | null>(null);
  const { userInfo, refetchUserInfo } = useUser();

  const loadAllAliases = async () => {
    const pageSize = 200;
    const firstResponse = await fetchAliasList({ skip: 0, limit: pageSize });

    if (firstResponse.code !== 0) {
      return firstResponse;
    }

    const total = firstResponse.result.total || 0;
    const names = firstResponse.result.names || [];

    for (let skip = names.length; skip < total; skip += pageSize) {
      const response = await fetchAliasList({ skip, limit: pageSize });
      if (response.code !== 0) {
        return response;
      }
      names.push(...(response.result.names || []));
    }

    return {
      ...firstResponse,
      result: {
        ...firstResponse.result,
        total,
        names,
      },
    };
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [aliasResponse, statsResponse] = await Promise.all([
        loadAllAliases(),
        fetchDailySendStats()
      ]);

      if (aliasResponse.code === 0) {
        setAliases(aliasResponse.result.names || []);
        setTotalAliases(aliasResponse.result.total);
      }

      if (statsResponse.code === 0) {
        setDailyStats(statsResponse.result.times || {});
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const validateAlias = (alias: string) => {
    if (!alias) return t('aliases.createModal.errors.empty');
    if (alias.length > 64) return t('aliases.createModal.errors.tooLong');
    if (!/^[a-zA-Z0-9-_]+$/.test(alias)) return t('aliases.createModal.errors.invalidChars');
    if (aliases.some(a => a.alias === alias)) return t('aliases.createModal.errors.exists');
    return '';
  };

  const handleCreateAlias = async () => {
    const error = validateAlias(newAlias);
    if (error) {
      setCreateError(error);
      return;
    }

    setCreating(true);
    setCreateError('');

    try {
      const flowId = aliasCreateFlowId || createAnalyticsFlowId('alias-create');
      const response = await createAlias(newAlias, {
        from: 'aliases_page',
        flowId,
        sessionId: getAnalyticsSessionId(),
      });
      if (response.code === 0) {
        await loadData();
        await refetchUserInfo();
        setIsCreateModalOpen(false);
        setNewAlias('');
      } else {
        setCreateError((response as any).err ? t((response as any).err) : (response.message || t('aliases.createModal.errors.failed')));
      }
    } catch (error) {
      setCreateError(t('aliases.createModal.errors.failed'));
    } finally {
      setCreating(false);
    }
  };

  const getDailyCount = (alias: string) => {
    return dailyStats[`${alias}_total`] || 0;
  };

  const filteredAliases = aliases.filter(alias =>
    alias.alias.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getFullAliasAddress = (alias: string) => {
    if (!userInfo) return alias;
    return `${alias}@${userInfo.name}.${userInfo.domain}`;
  };

  const handleOpenCreateAlias = () => {
    if (isAliasLimitReached(userInfo, totalAliases)) {
      const flowId = createAnalyticsFlowId('alias-create');
      setAliasCreateFlowId(flowId);
      setIsAliasLimitModalOpen(true);
      trackProductEvent('alias_create_blocked_limit', {
        source: 'aliases_page',
        userId: userInfo?.uid,
        flowId,
        totalAliases,
      });
      return;
    }
    const flowId = createAnalyticsFlowId('alias-create');
    setAliasCreateFlowId(flowId);
    trackProductEvent('alias_create_open', {
      source: 'aliases_page',
      userId: userInfo?.uid,
      flowId,
      totalAliases,
    });
    setIsCreateModalOpen(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    trackProductEvent('alias_copy', {
      source: 'aliases_page',
      userId: userInfo?.uid,
    });
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedAliases(filteredAliases.map(alias => alias.alias));
    } else {
      setSelectedAliases([]);
    }
  };

  const handleSelectAlias = (alias: string) => {
    setSelectedAliases(prev => {
      if (prev.includes(alias)) {
        return prev.filter(a => a !== alias);
      } else {
        return [...prev, alias];
      }
    });
  };

  const handleBulkStatusUpdate = async () => {
    if (selectedAliases.length === 0) return;

    const firstSelectedAlias = aliases.find(a => a.alias === selectedAliases[0]);
    const newStatus = firstSelectedAlias?.enable ? 'disable' : 'enable';

    try {
      const response = await updateAliasStatus(selectedAliases.join(','), newStatus);
      if (response.code === 0) {
        await loadData();
        setSelectedAliases([]);
      }
    } catch (error) {
      console.error('Error updating alias status:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedAliases.length === 0) return;

    if (window.confirm(t('aliases.actions.confirmDelete'))) {
      try {
        const response = await deleteAliases(selectedAliases.join(','));
        if (response.code === 0) {
          await loadData();
          await refetchUserInfo();
          setSelectedAliases([]);
        }
      } catch (error) {
        console.error('Error deleting aliases:', error);
      }
    }
  };

  if (loading) {
    return (
      <DashboardLayout
        isPlanModalOpen={isPlanModalOpen}
        onPlanModalClose={() => setIsPlanModalOpen(false)}
        onOpenPlanModal={() => setIsPlanModalOpen(true)}
      >
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  const firstSelectedAliasStatus = selectedAliases.length > 0
    ? aliases.find(a => a.alias === selectedAliases[0])?.enable
    : null;

  return (
    <DashboardLayout
      isPlanModalOpen={isPlanModalOpen}
      onPlanModalClose={() => setIsPlanModalOpen(false)}
      onOpenPlanModal={() => setIsPlanModalOpen(true)}
    >
      {/* Create Alias Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">{t('aliases.createModal.title')}</h3>
              <button onClick={() => setIsCreateModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('aliases.createModal.aliasName')}
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  value={newAlias}
                  onChange={(e) => {
                    setNewAlias(e.target.value.toLowerCase());
                    setCreateError('');
                  }}
                  className="flex-1 border border-gray-300 rounded-l-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('aliases.createModal.enterAlias')}
                />
                <span className="bg-gray-100 px-3 py-2 border border-l-0 border-gray-300 rounded-r-md text-gray-500">
                  @{userInfo?.name}.{userInfo?.domain}
                </span>
              </div>
              {createError && (
                <p className="mt-2 text-sm text-red-600">{createError}</p>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateAlias}
                disabled={creating}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating ? t('common.loading') : t('common.createNew')}
              </button>
            </div>
          </div>
        </div>
      )}

      {isAliasLimitModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('aliases.limitModal.title')}
              </h3>
              <button onClick={() => setIsAliasLimitModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-6 space-y-2 text-sm text-gray-700">
              <p>{t('aliases.limitModal.created', { count: totalAliases })}</p>
              <p>{t('aliases.limitModal.freeLimit')}</p>
              <p>{t('aliases.limitModal.upgradePrompt')}</p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsAliasLimitModalOpen(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => {
                  setIsAliasLimitModalOpen(false);
                  setIsPlanModalOpen(true);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {t('aliases.limitModal.upgradeNow')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Toolbar */}
      {selectedAliases.length > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-lg border border-gray-200 px-4 py-3 flex items-center gap-4 z-50">
          <span className="text-sm text-gray-600">
            {t('common.selected', { count: selectedAliases.length })}
          </span>
          <button
            onClick={handleBulkStatusUpdate}
            className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
          >
            <Ban className="h-4 w-4 mr-1" />
            {firstSelectedAliasStatus ? t('common.disable') : t('common.enable')}
          </button>
          <button
            onClick={handleBulkDelete}
            className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            {t('aliases.actions.delete')}
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">{t('aliases.title')}</h1>
              <p className="text-sm text-gray-600 mt-1">{t('aliases.total', { count: totalAliases })}</p>
            </div>
            <button 
              onClick={handleOpenCreateAlias}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-5 w-5 mr-2" />
              {t('aliases.createNew')}
            </button>
          </div>
          
          <div className="mt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('aliases.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedAliases.length === filteredAliases.length}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('aliases.table.alias')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('aliases.table.created')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('aliases.table.forwarded')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('aliases.table.today')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('aliases.table.status')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('aliases.table.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAliases.map((alias) => (
                <tr key={alias.alias} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedAliases.includes(alias.alias)}
                      onChange={() => handleSelectAlias(alias.alias)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900">{getFullAliasAddress(alias.alias)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-500">
                      {new Date(alias.time * 1000).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-500">{alias.fwd}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-500">{getDailyCount(alias.alias)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      alias.enable ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {alias.enable ? t('common.active') : t('common.disabled')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => copyToClipboard(getFullAliasAddress(alias.alias))}
                      className="text-gray-400 hover:text-gray-500 mx-2"
                      title={t('common.copy')}
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredAliases.length === 0 && (
          <div className="text-center py-12">
            <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('aliases.empty.title')}</h3>
            <p className="text-gray-500">
              {searchTerm ? t('aliases.empty.searchEmpty') : t('aliases.empty.noAliases')}
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AliasesPage;
